import React, { memo, useContext } from 'react';
import { TransactionsContext } from '../context/transactions.context';
import { calcExpInc, formatAmount, calcBarPercent } from '../helpers';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useStyles from './styles/transactionsSummary.styles';
import Collapse from '@material-ui/core/Collapse';
import { CurrencyContext } from '../context/currency.context';
import LinearProgress from '@material-ui/core/LinearProgress';

function TransactionsSummary() {
	const classes = useStyles();
	const transactions = useContext(TransactionsContext);
	const currency = useContext(CurrencyContext);
	const up600 = useMediaQuery('(min-width:600px)');

	const renderTotal = () => {
		const percentage =
			totalInc && totalExp && !up600 ? ` (${parseInt(total / totalInc * 100)}%)` : '';

		const dec = (total > 0 && total >= 1000) || (total < 0 && total <= -1000) ? 0 : 2;
		return formatAmount(total, currency, dec) + percentage;
	};

	const [ totalExp, totalInc ] = calcExpInc(transactions);
	const total = totalInc + totalExp;

	const decimal = totalInc >= 1000 || totalExp <= -1000 ? 0 : 2;

	const barPercentage =
		totalInc >= totalExp
			? 100 - calcBarPercent(totalExp, totalInc)
			: 100 - calcBarPercent(totalInc, totalExp);

	return (
		<Collapse in={totalInc !== 0 || totalExp !== 0} timeout={700}>
			<Paper className={classes.root}>
				{!up600 && (
					<LinearProgress
						color={totalExp > totalInc ? 'secondary' : 'primary'}
						variant="determinate"
						className={classes.overallBar}
						value={barPercentage}
					/>
				)}
				{up600 && (
					<div className={classes.report}>
						<Typography variant="h3">
							{totalInc !== 0 ? `${parseInt(total / totalInc * 100)}%` : '0%'}
						</Typography>
						<Typography align="center" variant="body2">
							{totalExp * -1 <= totalInc || totalInc === 0 ? (
								`Saved of `
							) : (
								`Spent more than `
							)}
							your income
						</Typography>
					</div>
				)}
				<List className={classes.list}>
					<ListItem className={classes.listItemTop}>
						<ListItemText className={classes.itemTextLeft} primary={'Income'} />
						{up600 && (
							<LinearProgress
								variant="determinate"
								className={classes.bar}
								value={calcBarPercent(totalInc, totalExp)}
							/>
						)}
						<ListItemText
							className={classes.itemTextRight}
							primary={formatAmount(totalInc, currency, decimal)}
							primaryTypographyProps={{ color: 'primary', variant: 'h6' }}
						/>
					</ListItem>
					<ListItem className={classes.listItemBottom}>
						<ListItemText className={classes.itemTextLeft} primary={'Expenses'} />
						{up600 && (
							<LinearProgress
								variant="determinate"
								color="secondary"
								className={classes.bar}
								value={calcBarPercent(totalExp, totalInc)}
							/>
						)}
						<ListItemText
							className={classes.itemTextRight}
							primary={formatAmount(totalExp, currency, decimal)}
							primaryTypographyProps={{ color: 'secondary', variant: 'h6' }}
						/>
					</ListItem>
					<Divider variant="middle" />
					<ListItem className={classes.listItemTotal}>
						<ListItemText
							className={clsx(classes.itemTextLeft, classes.totalTitle)}
							primary={`${total < 0 ? 'Spent' : 'Saved'} this month`}
						/>
						<ListItemText
							className={clsx(classes.itemTextRight, classes.totalAmount)}
							primary={renderTotal()}
							primaryTypographyProps={{ color: total < 0 ? 'secondary' : 'primary' }}
						/>
					</ListItem>
				</List>
			</Paper>
		</Collapse>
	);
}

export default memo(TransactionsSummary);
